import React, { forwardRef, useLayoutEffect, useRef } from "react";
import { TextInput } from "@mantine/core";
import { useAtom } from "jotai";
import { projectDetailsAtom } from "../../utils/atoms/projectDetailsAtom";
import { formatProjectNumber } from "../../utils/helpers";

const ProjectNumberInput = forwardRef(({ value, onChange, onFocus, onBlur, ...props }, ref) => {
  const [projectDetails] = useAtom(projectDetailsAtom);
  const numberFormat = projectDetails?.projectDTO?.numberFormat;
  const pendingCaretRef = useRef(null);
  const rawValue = value ?? "";
  const decimalSeparator = numberFormat === "1.234.567,89" || numberFormat === "1 234 567,89"
    ? ","
    : ".";
  const formatInputValue = (input) => {
    const text = String(input);
    if (!["", "-", "+", ".", "-.", "+."].includes(text)) {
      const match = text.match(/^([+-]?)(\d*)(?:\.(\d*))?$/);
      if (match) {
        const [, sign, integerPart, fractionPart] = match;
        const normalizedIntegerPart = (integerPart || "0").replace(/^0+(?=\d)/, "");
        const groupedInteger = formatProjectNumber(`${sign}${normalizedIntegerPart}`, { numberFormat });
        return `${groupedInteger}${text.includes(".") ? `${decimalSeparator}${fractionPart}` : ""}`;
      }
    }
    return text;
  };
  const displayValue = formatInputValue(rawValue);

  const normalizeInputValue = (input) => numberFormat === "1.234.567,89"
    ? input.replaceAll(".", "").replaceAll(",", ".")
    : numberFormat === "1 234 567,89"
      ? input.replaceAll(" ", "").replaceAll(",", ".")
      : input.replaceAll(",", "");

  const getDisplayCaretPosition = (display, logicalPosition) => {
    if (logicalPosition <= 0) return 0;
    let logicalIndex = 0;
    for (let index = 0; index < display.length; index++) {
      const character = display[index];
      if (/\d/.test(character) || character === "+" || character === "-" || character === decimalSeparator) {
        logicalIndex++;
        if (logicalIndex === logicalPosition) return index + 1;
      }
    }
    return display.length;
  };

  useLayoutEffect(() => {
    const pendingCaret = pendingCaretRef.current;
    if (!pendingCaret) return;
    const start = getDisplayCaretPosition(displayValue, pendingCaret.start);
    const end = getDisplayCaretPosition(displayValue, pendingCaret.end);
    pendingCaret.input.setSelectionRange(start, end, pendingCaret.direction);
    pendingCaretRef.current = null;
  });

  const handleChange = (event) => {
    const input = event.target.value;
    const selectionStart = event.target.selectionStart ?? input.length;
    const selectionEnd = event.target.selectionEnd ?? selectionStart;
    pendingCaretRef.current = {
      input: event.target,
      start: normalizeInputValue(input.slice(0, selectionStart)).length,
      end: normalizeInputValue(input.slice(0, selectionEnd)).length,
      direction: event.target.selectionDirection || "none",
    };
    const rawInput = normalizeInputValue(input);
    const nextEvent = {
      ...event,
      target: { ...event.target, value: rawInput },
      currentTarget: { ...event.currentTarget, value: rawInput },
    };
    onChange?.(nextEvent);
  };

  return (
    <TextInput
      {...props}
      ref={ref}
      type="text"
      inputMode="decimal"
      value={displayValue}
      onFocus={(event) => {
        onFocus?.(event);
      }}
      onChange={handleChange}
      onBlur={(event) => {
        onBlur?.(event);
      }}
    />
  );
});

ProjectNumberInput.displayName = "ProjectNumberInput";

export default ProjectNumberInput;